import { useEffect, useRef } from 'react';

type PulseFieldAttributes = {
  /** Replaces the field's id and points the field's `<label for>` at it */
  id?: string;
  invalid?: boolean;
  required?: boolean;
  testId?: string;
};

const toggleAttribute = (element: HTMLElement, name: string, on?: boolean) => {
  if (on) {
    element.setAttribute(name, 'true');
  } else {
    element.removeAttribute(name);
  }
};

/**
 * Pulse UI 0.26 fields don't accept every attribute a CMS form needs: TextArea and DatePicker use
 * their name or label as the element id (so two CMS forms on a page, or two fields with the same
 * label, clash), and TextArea, SelectField, DatePicker, radio groups and checkboxes don't forward
 * `aria-invalid`, `aria-required` or `data-testid`. Put the returned ref on a wrapper around the
 * Pulse field and this sets them on the element matching `selector` after each render. React
 * leaves them alone because the props it passes to Pulse never set them.
 * TODO: pass these as props once Pulse UI accepts them, and drop this.
 */
const usePulseFieldAttributes = <T extends HTMLElement = HTMLDivElement>(
  selector: string,
  { id, invalid, required, testId }: PulseFieldAttributes,
) => {
  const containerRef = useRef<T>(null);

  // no dependency list: Pulse can re-create its elements (e.g. a remounted radio group)
  useEffect(() => {
    const container = containerRef.current;
    const field = container?.querySelector<HTMLElement>(selector);
    if (!container || !field) {
      return;
    }

    if (id && field.id !== id) {
      const label = Array.from(container.querySelectorAll('label')).find(
        ({ htmlFor }) => htmlFor === field.id,
      );
      if (label) {
        label.htmlFor = id;
      }
      field.id = id;
    }
    if (invalid !== undefined) {
      toggleAttribute(field, 'aria-invalid', invalid);
    }
    if (required !== undefined) {
      toggleAttribute(field, 'aria-required', required);
    }
    if (testId) {
      field.dataset.testid = testId;
    }
  });

  return containerRef;
};

export default usePulseFieldAttributes;
